import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { createContext, useContext, useState } from 'react';
const DialogContext = createContext(undefined);
export const useDialog = () => {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('useDialog must be used within DialogProvider');
    }
    return ctx;
};
const dialogConfig = {
    success: {
        icon: CheckCircleIcon,
        iconColor: 'success.main',
        iconSize: 54,
    },
    confirm: {
        icon: WarningAmberIcon,
        iconColor: 'warning.main',
        iconSize: 54,
    },
    error: {
        icon: ErrorOutlineIcon,
        iconColor: 'error.main',
        iconSize: 54,
    },
    warning: {
        icon: WarningAmberIcon,
        iconColor: 'warning.main',
        iconSize: 54,
    },
    info: {
        icon: InfoOutlinedIcon,
        iconColor: 'info.main',
        iconSize: 54,
    },
};
export const DialogProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [options, setOptions] = useState({});
    const showDialog = (msg, opts = {}) => {
        setMessage(msg);
        setOptions({ type: 'success', ...opts });
        setOpen(true);
    };
    const showConfirmDialog = (msg, onConfirm, opts = {}) => {
        setMessage(msg);
        setOptions({ type: 'confirm', onConfirm, ...opts });
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        if (options.onClose) {
            options.onClose();
        }
        if (options.onCancel) {
            options.onCancel();
        }
    };
    const handleConfirm = () => {
        setOpen(false);
        if (options.onConfirm) {
            options.onConfirm();
        }
    };
    const type = options.type || 'success';
    const config = dialogConfig[type];
    const IconComponent = config.icon;
    const isConfirm = type === 'confirm';
    return (_jsxs(DialogContext.Provider, { value: { showDialog, showConfirmDialog }, children: [children, _jsx(Dialog, { open: open, onClose: handleClose, maxWidth: "xs", fullWidth: true, slotProps: {
                    paper: {
                        sx: {
                            borderRadius: 2,
                            padding: 2,
                        },
                    },
                }, children: _jsx(DialogContent, { children: _jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                        }, children: [_jsx(IconComponent, { sx: {
                                    fontSize: config.iconSize,
                                    color: config.iconColor,
                                } }), _jsx(Box, { sx: { textAlign: 'center' }, py: 2, children: _jsx(Typography, { variant: "subtitle2", color: "primary", children: options.title || message }) }), isConfirm ? (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    gap: 2,
                                    width: '100%',
                                    justifyContent: 'center',
                                }, children: [_jsx(Button, { variant: "outlined", onClick: handleClose, sx: { minWidth: 120 }, children: options.cancelText || 'CANCEL' }), _jsx(Button, { variant: "contained", color: "error", onClick: handleConfirm, sx: { minWidth: 120 }, children: options.confirmText || 'CONFIRM' })] })) : (_jsx(Button, { variant: "outlined", onClick: handleClose, sx: { width: 180 }, children: options.buttonText || 'DONE' }))] }) }) })] }));
};
